/**
 * Utilitários de pacotes para o fluxo de agendamento web (PRIT-4029).
 * Funções puras — sem dependência de framework ou rede.
 */

// ─── Helpers internos ─────────────────────────────────────────────────────────

const centavosParaReais = (centavos) => (centavos || 0) / 100;

// O nome do pricingType vem no idioma do negócio
const DONT_SHOW_PRICING_TYPE_NAMES = ["Não mostrar", "Don't show", "No mostrar"];
const FIXED_PRICING_TYPE_NAMES = ["Fixo", "Fixed", "Fijado"];

/**
 * Preço por sessão de uma oferta em reais, arredondado em 2 casas.
 * @param {{ price: number, quantity: number }} offer
 */
export const pricePerSession = (offer) =>
    Math.round((centavosParaReais(offer.price) / (offer.quantity || 1)) * 100) / 100;

/**
 * Verifica se uma oferta de pacote do negócio é elegível para exibição (RN-12).
 * @param {object} pkg
 * @returns {boolean}
 */
export const isOfferEligible = (pkg) => {
    if (!pkg) return false;
    if (pkg.deletedAt) return false;
    if (pkg.expiresAt && new Date(pkg.expiresAt) < new Date()) return false;
    if (DONT_SHOW_PRICING_TYPE_NAMES.includes(pkg.pricingType?.name)) return false;
    return true;
};

// ─── REQ-01 ───────────────────────────────────────────────────────────────────

/**
 * Gera o dado do rótulo "Com pacote" para a lista de serviços (RF-01).
 *
 * @param {Array} businessPackages  Lista plana de packages da API
 * @param {string} serviceId
 * @returns {{ pricePerSession: number, multipleOffers: boolean } | null}
 */
export const buildPackageLabel = (businessPackages, serviceId) => {
    if (!businessPackages || !businessPackages.length) return null;

    const eligible = businessPackages.filter(
        (pkg) => pkg.service?.id === serviceId && isOfferEligible(pkg)
    );

    if (eligible.length === 0) return null;

    const sorted = eligible.slice().sort((a, b) => pricePerSession(a) - pricePerSession(b));
    const best = sorted[0];

    return {
        pricePerSession: pricePerSession(best),
        multipleOffers: eligible.length > 1,
    };
};

// ─── REQ-02 / REQ-03 ──────────────────────────────────────────────────────────

/**
 * Filtra e ordena os pacotes do cliente elegíveis para um serviço (RN-13).
 *
 * @param {Array}  clientPackageGroups  Resposta de active_package_sessions
 * @param {string} serviceId
 * @param {string} professionalId
 * @param {string} bookingDate          ISO 8601 da data/hora do agendamento
 * @returns {Array} compras elegíveis, ordenadas pela sessão que vence primeiro
 */
export const getEligibleClientPackages = (
    clientPackageGroups,
    serviceId,
    professionalId,
    bookingDate
) => {
    if (!clientPackageGroups?.length) return [];

    // O profissional pode vir no grupo ou em cada compra, conforme o contrato da API
    const groups = clientPackageGroups.filter((g) => g.serviceId === serviceId);
    if (!groups.length) return [];

    const bookingTime = new Date(bookingDate).getTime();

    const packages = [].concat(...groups.map((g) =>
        (g.packages || []).map((pkg) => ({
            ...pkg,
            professionalId: pkg.professionalId ?? g.professionalId,
            professionalName: pkg.professionalName ?? g.professionalName,
            serviceId: g.serviceId,
            serviceName: g.serviceName,
        }))
    ));

    const eligible = packages.filter((pkg) => {
        if (pkg.professionalId !== professionalId) return false;
        if (!pkg.remainingSessions || pkg.remainingSessions <= 0) return false;
        const hasValidSession = (pkg.sessions || []).some(
            (s) => s.expiresAt && new Date(s.expiresAt).getTime() > bookingTime
        );
        return hasValidSession;
    });

    // Ordena pela sessão válida que vence primeiro (RN-13)
    eligible.sort((a, b) => {
        const minA = Math.min(
            ...(a.sessions || [])
                .filter((s) => s.expiresAt && new Date(s.expiresAt).getTime() > bookingTime)
                .map((s) => new Date(s.expiresAt).getTime())
        );
        const minB = Math.min(
            ...(b.sessions || [])
                .filter((s) => s.expiresAt && new Date(s.expiresAt).getTime() > bookingTime)
                .map((s) => new Date(s.expiresAt).getTime())
        );
        return minA - minB;
    });

    return eligible;
};

/**
 * Retorna a menor data de expiração entre as sessões válidas de uma compra.
 * @param {object} clientPkg  item de packages[] de active_package_sessions
 * @param {string} bookingDate
 * @returns {string|null} ISO 8601
 */
export const getEarliestValidExpiry = (clientPkg, bookingDate) => {
    const bookingTime = new Date(bookingDate).getTime();
    const valids = (clientPkg.sessions || []).filter(
        (s) => s.expiresAt && new Date(s.expiresAt).getTime() > bookingTime
    );
    if (!valids.length) return null;
    valids.sort((a, b) => new Date(a.expiresAt) - new Date(b.expiresAt));
    return valids[0].expiresAt;
};

// ─── REQ-03 ───────────────────────────────────────────────────────────────────

/**
 * Monta a lista ordenada de opções para o sheet de escolha do pacote (RN-04).
 *
 * Ordem: pacotes do cliente → compras elegíveis (menor price/session primeiro) → avulso.
 * O avulso é retornado separado (type: "avulso") mas não entra nos cards.
 *
 * @param {Array}  clientPackages    resultado de getEligibleClientPackages
 * @param {Array}  businessPackages  packagesList() já filtrado para o serviço
 * @param {object} service           { id, price } — preço em centavos
 * @returns {Array<{type, id, ...}>}
 */
export const sortPackageOptions = (clientPackages, businessPackages, service) => {
    const options = [];

    // 1. Pacotes do cliente
    (clientPackages || []).forEach((pkg) => {
        options.push({
            type: "client",
            id: `client:${pkg.bookingTransactionId}`,
            clientPackage: pkg,
            // Não exibe radio quando é o único item (handled no componente)
        });
    });

    // 2. Ofertas de compra (só se cliente não tem pacote — RN-03)
    if (!clientPackages?.length) {
        const eligible = (businessPackages || []).filter(isOfferEligible);
        const sorted = eligible.slice().sort((a, b) => pricePerSession(a) - pricePerSession(b));

        sorted.forEach((offer) => {
            const servicePrice = centavosParaReais(service?.price || 0);
            const offerTotal = centavosParaReais(offer.price);
            const rawSavings = servicePrice * (offer.quantity || 1) - offerTotal;
            options.push({
                type: "purchase",
                id: `purchase:${offer.id}`,
                offer,
                pricePerSession: pricePerSession(offer),
                savings: rawSavings > 0 ? Math.round(rawSavings * 100) / 100 : 0,
            });
        });
    }

    return options;
};

// ─── REQ-05 ───────────────────────────────────────────────────────────────────

/**
 * Calcula os valores financeiros da revisão (RN-05).
 *
 * @param {Array}  services       serviços agendados, com `price` e `sinal` em centavos
 * @param {object} packageChoices mapa { [serviceId]: { type, package?, offer? } }
 * @param {object} [options]
 * @param {boolean} [options.chargePendingPackages=true] sem política de cancelamento ativa,
 *        o pacote pendente não é cobrado no agendamento
 * @returns {{ pagarAgora: number, restanteNoLocal: number }} em reais
 */
export const calculateReviewValues = (services, packageChoices = {}, { chargePendingPackages = true } = {}) => {
    let pagarAgora = 0;
    let restanteNoLocal = 0;

    (services || []).forEach((service) => {
        const choice = packageChoices[service.id] || { type: "avulso" };
        const preco = centavosParaReais(service.price);
        const sinal = centavosParaReais(service.sinal);

        switch (choice.type) {
            case "client": {
                const pkg = choice.clientPackage || choice.package || {};
                if (chargePendingPackages && pkg.paymentStatus !== "completed") {
                    // Pendente: paga o valor total do pacote agora
                    pagarAgora += centavosParaReais(clientPackageTotalPrice(pkg));
                }
                break;
            }
            case "purchase": {
                // Compra de pacote novo: paga o total do pacote agora
                const offer = choice.offer || choice.package || {};
                pagarAgora += centavosParaReais(offer.price);
                break;
            }
            default: {
                // Avulso: paga sinal agora, restante no local
                pagarAgora += sinal;
                restanteNoLocal += preco - sinal;
                break;
            }
        }
    });

    return {
        pagarAgora: Math.round(pagarAgora * 100) / 100,
        restanteNoLocal: Math.round(restanteNoLocal * 100) / 100,
    };
};

// ─── REQ-07 ───────────────────────────────────────────────────────────────────

/**
 * Retorna true se o pacote do cliente está pago (paymentStatus === "completed").
 * @param {object|null} pkg
 */
export const isClientPackagePaid = (pkg) => {
    if (!pkg) return false;
    return pkg.paymentStatus === "completed";
};

/**
 * Formata data ISO 8601 para DD/MM/AAAA no fuso local (MM/DD/AAAA em inglês).
 * @param {string} iso
 * @param {string} [language]  idioma do i18n (ex.: "en-US")
 * @returns {string}
 */
export const formatDateDDMMYYYY = (iso, language) => {
    if (!iso) return "";
    const d = new Date(iso);
    const day   = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year  = d.getFullYear();
    if (String(language || "").toLowerCase().startsWith("en")) return `${month}/${day}/${year}`;
    return `${day}/${month}/${year}`;
};

// ─── Escolha de pacote → checkout ─────────────────────────────────────────────

/**
 * Valor total (centavos) de uma compra do cliente: o preço vem por sessão.
 * @param {object} clientPkg  item de packages[] de active_package_sessions
 */
export const clientPackageTotalPrice = (clientPkg) => {
    if (!clientPkg) return 0;
    if (clientPkg.price) return clientPkg.price;
    return (clientPkg.sessions || []).reduce((sum, s) => sum + (parseFloat(s.price) || 0), 0);
};

/**
 * Nome e quantidade de uma compra do cliente — a API manda dentro de `package`.
 * @param {object} clientPkg
 * @returns {{ name: string, quantity: number }}
 */
export const clientPackageInfo = (clientPkg) => ({
    name: clientPkg?.package?.name || clientPkg?.name || clientPkg?.packageName || "",
    quantity: clientPkg?.package?.quantity || clientPkg?.quantity || 0,
});

/**
 * Monta a escolha de um serviço no sheet de pacote, já com os dados de exibição
 * usados no checkout e na confirmação.
 *
 * @param {object} option       item de sortPackageOptions (client | purchase) ou { type: "avulso" }
 * @param {string} bookingDate  ISO 8601 da data/hora do agendamento
 * @param {string} [language]   idioma do i18n, para a data de validade
 * @returns {{ type, clientPackage?, offer?, display? }}
 */
export const buildPackageChoice = (option, bookingDate, language) => {
    if (!option || option.type === "avulso") return { type: "avulso" };

    if (option.type === "client") {
        const pkg = option.clientPackage;
        const { name, quantity } = clientPackageInfo(pkg);
        const expiry = getEarliestValidExpiry(pkg, bookingDate);
        return {
            type: "client",
            clientPackage: pkg,
            display: {
                nome: name,
                total: quantity,
                feitas: Math.max(0, quantity - (pkg.remainingSessions || 0)),
                valeAte: expiry ? formatDateDDMMYYYY(expiry, language) : null,
                pago: isClientPackagePaid(pkg),
                novo: false,
                preco: centavosParaReais(clientPackageTotalPrice(pkg)),
            },
        };
    }

    const offer = option.offer;
    const valeAte = offer?.bookingExpirationDays
        ? formatDateDDMMYYYY(
              new Date(Date.now() + offer.bookingExpirationDays * 24 * 60 * 60 * 1000).toISOString(),
              language
          )
        : null;
    return {
        type: "purchase",
        offer,
        display: {
            nome: offer?.name || "",
            total: offer?.quantity || 0,
            feitas: 0,
            valeAte,
            pago: false,
            novo: true,
            preco: centavosParaReais(offer?.price),
        },
    };
};

/**
 * Converte a escolha "usar meu pacote" no item de `sessionsAccepted` que o checkout
 * já sabe agendar (mesmo formato do CheckoutSessionPackageModal).
 *
 * @param {object} choice           resultado de buildPackageChoice com type "client"
 * @param {object} checkoutService  serviço no formato do payload do checkout
 * @returns {object|null}
 */
export const buildSessionAcceptedFromChoice = (choice, checkoutService) => {
    const pkg = choice?.clientPackage;
    if (!pkg || !checkoutService) return null;

    const bookingTime = new Date(checkoutService.date).getTime();
    const validSessions = (pkg.sessions || [])
        .filter((s) => s.expiresAt && new Date(s.expiresAt).getTime() > bookingTime)
        .sort((a, b) => new Date(a.expiresAt) - new Date(b.expiresAt));
    const session = validSessions[0];
    const bookingId = session?.bookingId ?? session?.id;
    if (!bookingId) return null;

    const duration = checkoutService.professional?.duration || checkoutService.service?.serviceDuration || 60;
    const { name, quantity } = clientPackageInfo(pkg);

    return {
        bookingId,
        serviceId: checkoutService.service_id,
        payload: {
            start_time: checkoutService.date,
            end_time: new Date(bookingTime + duration * 60000).toISOString(),
            acesso: "web",
            answer: "",
        },
        infoPackage: {
            packageName: name,
            packageQuantity: quantity,
            packageRemainding: pkg.remainingSessions,
            servicePricePackage: session?.price,
            paymentStatus: pkg.paymentStatus,
            packageTotalPrice: clientPackageTotalPrice(pkg),
            packageExpiry: choice.display?.valeAte || null,
        },
        services: [checkoutService],
    };
};

/**
 * Com política de cancelamento ativa, avulso de preço fixo exige pagamento próprio.
 * Não existe endpoint que compre pacote e pague avulso juntos, então nesse caso
 * a compra de pacote não é oferecida.
 *
 * @param {boolean} isPolicyActive
 * @param {string[]} otherServicePricingTypes  pricingType dos outros serviços (avulsos) do agendamento
 */
export const canOfferPackagePurchase = (isPolicyActive, otherServicePricingTypes = []) =>
    !isPolicyActive || !otherServicePricingTypes.some((type) => FIXED_PRICING_TYPE_NAMES.includes(type));

/**
 * Pacote com pagamento pendente é cobrado no agendamento quando a política está
 * ativa, e essa cobrança não inclui outros serviços. Com mais de um serviço no
 * agendamento, o pacote pendente não é oferecido.
 *
 * @param {Object} pkg             pacote/sessão da cliente (usa paymentStatus)
 * @param {boolean} isPolicyActive
 * @param {number} servicesCount   quantidade de serviços no agendamento
 */
export const canOfferPendingPackage = (pkg, isPolicyActive, servicesCount) =>
    isClientPackagePaid(pkg) || !isPolicyActive || servicesCount <= 1;
